import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Input,
  Modal,
  Form,
  Select,
  InputNumber,
  Switch,
  Tag,
  Space,
  Tooltip,
  Popconfirm,
  Typography,
  message,
  Row,
  Col,
  Card,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CopyOutlined,
  ReloadOutlined,
  ExclamationCircleOutlined
} from '@ant-design/icons';
import { productosService, categoriasService } from '../services/api';
import { useAuth } from '../context/AuthContext';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const UNIDADES = ['PZA', 'KG', 'LT', 'MT', 'CM', 'M2', 'M3', 'CJ', 'PAQ', 'BTE', 'PAR', 'SET', 'ROLLO', 'OTRO'];

const formatCurrency = (amount) => {
  if (amount == null || amount === '') return '—';
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 2
  }).format(amount);
};

function getStockColor(stockActual, stockMinimo) {
  const actual = parseFloat(stockActual) || 0;
  const minimo = parseFloat(stockMinimo) || 0;
  if (actual <= minimo) return { color: '#ff4d4f', class: 'stock-critico' };
  if (actual <= minimo * 1.5) return { color: '#faad14', class: 'stock-bajo' };
  return { color: '#52c41a', class: 'stock-ok' };
}

function Productos() {
  const { user } = useAuth();
  const canEdit = user?.rol === 'root' || user?.rol === 'admin';
  const canDelete = user?.rol === 'root';

  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  const fetchProductos = useCallback(async (searchTerm = '') => {
    setLoading(true);
    try {
      const res = await productosService.getAll({ search: searchTerm });
      setProductos(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar productos');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategorias = useCallback(async () => {
    try {
      const res = await categoriasService.getAll();
      setCategorias(res.data.data || []);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchProductos();
    fetchCategorias();
  }, [fetchProductos, fetchCategorias]);

  const handleSearch = (value) => {
    setSearch(value);
    fetchProductos(value);
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    form.resetFields();
    form.setFieldsValue({ activo: true, unidad_medida: 'PZA' });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingProduct(record);
    form.setFieldsValue({
      clave: record.clave,
      nombre: record.nombre,
      descripcion: record.descripcion,
      categoria_id: record.categoria_id,
      unidad_medida: record.unidad_medida,
      stock_minimo: parseFloat(record.stock_minimo),
      precio_compra: parseFloat(record.precio_compra),
      precio_venta: parseFloat(record.precio_venta),
      activo: Boolean(record.activo)
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      // Ensure clave is uppercase
      if (values.clave) values.clave = values.clave.toUpperCase();

      setSaving(true);
      if (editingProduct) {
        await productosService.update(editingProduct.id, values);
        message.success('Producto actualizado exitosamente');
      } else {
        await productosService.create(values);
        message.success('Producto creado exitosamente');
      }
      setModalOpen(false);
      fetchProductos(search);
    } catch (error) {
      if (error.errorFields) return; // validation error
      message.error(error.displayMessage || 'Error al guardar producto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await productosService.delete(record.id);
      message.success(`Producto "${record.nombre}" desactivado`);
      fetchProductos(search);
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar producto');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`Clave "${text}" copiada al portapapeles`);
    });
  };

  const columns = [
    {
      title: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 130,
      sorter: (a, b) => a.clave.localeCompare(b.clave),
      render: (clave) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }}>{clave}</Text>
          <Tooltip title="Copiar clave">
            <CopyOutlined
              style={{ cursor: 'pointer', color: '#1677ff', fontSize: 12 }}
              onClick={() => copyToClipboard(clave)}
            />
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      sorter: (a, b) => a.nombre.localeCompare(b.nombre),
      ellipsis: true,
      render: (nombre, record) => (
        <Tooltip title={record.descripcion || nombre}>
          <span>{nombre}</span>
        </Tooltip>
      )
    },
    {
      title: 'Categoría',
      key: 'categoria_nombre',
      width: 130,
      render: (_, record) => record.categoria?.nombre
        ? <Tag>{record.categoria.nombre}</Tag>
        : <Text type="secondary">—</Text>
    },
    {
      title: 'Unidad',
      dataIndex: 'unidad_medida',
      key: 'unidad_medida',
      width: 80,
      align: 'center',
      render: (u) => <Tag color="default">{u}</Tag>
    },
    {
      title: 'Stock Actual',
      dataIndex: 'stock_actual',
      key: 'stock_actual',
      width: 120,
      align: 'right',
      sorter: (a, b) => parseFloat(a.stock_actual) - parseFloat(b.stock_actual),
      render: (val, record) => {
        const { color } = getStockColor(val, record.stock_minimo);
        return (
          <Text strong style={{ color }}>
            {parseFloat(val).toLocaleString('es-MX')}
          </Text>
        );
      }
    },
    {
      title: 'Stock Mín.',
      dataIndex: 'stock_minimo',
      key: 'stock_minimo',
      width: 100,
      align: 'right',
      render: (val) => parseFloat(val).toLocaleString('es-MX')
    },
    {
      title: 'P. Compra',
      dataIndex: 'precio_compra',
      key: 'precio_compra',
      width: 110,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'P. Venta',
      dataIndex: 'precio_venta',
      key: 'precio_venta',
      width: 110,
      align: 'right',
      render: (val) => formatCurrency(val)
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 90,
      align: 'center',
      filters: [
        { text: 'Activo', value: 1 },
        { text: 'Inactivo', value: 0 }
      ],
      onFilter: (value, record) => record.activo === value,
      render: (activo) =>
        activo ? (
          <Tag color="success">Activo</Tag>
        ) : (
          <Tag color="default">Inactivo</Tag>
        )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 110,
      fixed: 'right',
      render: (_, record) => (
        <Space size={4}>
          {canEdit && (
            <Tooltip title="Editar producto">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="¿Desactivar producto?"
              description={`El producto "${record.nombre}" será marcado como inactivo.`}
              onConfirm={() => handleDelete(record)}
              okText="Sí, desactivar"
              cancelText="Cancelar"
              icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
              okButtonProps={{ danger: true }}
              disabled={!record.activo}
            >
              <Tooltip title={record.activo ? 'Desactivar producto' : 'Ya está inactivo'}>
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  disabled={!record.activo}
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Productos</Title>
          <Text type="secondary">Gestión de productos e inventario</Text>
        </div>
      </div>

      {/* Filters & Actions */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={12} md={10} lg={8}>
            <Input
              placeholder="Buscar por nombre o clave..."
              prefix={<SearchOutlined />}
              allowClear
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (!e.target.value) fetchProductos('');
              }}
              onPressEnter={(e) => handleSearch(e.target.value)}
              onClear={() => handleSearch('')}
            />
          </Col>
          <Col>
            <Button
              icon={<SearchOutlined />}
              onClick={() => handleSearch(search)}
            >
              Buscar
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => { setSearch(''); fetchProductos(''); }}
            >
              Limpiar
            </Button>
          </Col>
          {canEdit && (
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                Nuevo Producto
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* Table */}
      <Card bordered={false}>
        <Table
          dataSource={productos}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: true,
            pageSizeOptions: ['10', '15', '25', '50'],
            showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} productos`,
            locale: { items_per_page: '/ página' }
          }}
          locale={{ emptyText: 'No se encontraron productos' }}
        />
      </Card>

      {/* Modal Create/Edit */}
      <Modal
        title={editingProduct ? `Editar Producto: ${editingProduct.clave}` : 'Nuevo Producto'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingProduct ? 'Guardar Cambios' : 'Crear Producto'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={700}
        destroyOnClose
      >
        <Divider style={{ marginTop: 8 }} />
        <Form
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={{ activo: true, unidad_medida: 'PZA' }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={10}>
              <Form.Item
                name="clave"
                label="Clave del Producto"
                rules={[
                  { required: true, message: 'La clave es requerida' },
                  { max: 50, message: 'Máximo 50 caracteres' },
                  { pattern: /^[A-Z0-9_\-]+$/, message: 'Solo letras mayúsculas, números, guiones y guión bajo' }
                ]}
                normalize={(value) => value?.toUpperCase()}
              >
                <Input
                  placeholder="Ej: PROD-001"
                  maxLength={50}
                  style={{ textTransform: 'uppercase' }}
                  onChange={(e) => {
                    form.setFieldValue('clave', e.target.value.toUpperCase());
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={14}>
              <Form.Item
                name="nombre"
                label="Nombre del Producto"
                rules={[
                  { required: true, message: 'El nombre es requerido' },
                  { max: 255, message: 'Máximo 255 caracteres' }
                ]}
              >
                <Input placeholder="Nombre completo del producto" maxLength={255} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="descripcion" label="Descripción">
            <TextArea
              placeholder="Descripción del producto (opcional)"
              rows={2}
              maxLength={1000}
              showCount
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="categoria_id" label="Categoría">
                <Select
                  placeholder="Selecciona una categoría"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    option?.children?.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {categorias.map((cat) => (
                    <Option key={cat.id} value={cat.id}>{cat.nombre}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="unidad_medida" label="Unidad de Medida" rules={[{ required: true }]}>
                <Select placeholder="Selecciona unidad">
                  {UNIDADES.map((u) => (
                    <Option key={u} value={u}>{u}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="stock_minimo"
                label="Stock Mínimo"
                rules={[{ type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  placeholder="0"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="precio_compra"
                label="Precio de Compra"
                rules={[{ type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="$"
                  placeholder="0.00"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="precio_venta"
                label="Precio de Venta"
                rules={[{ type: 'number', min: 0, message: 'Debe ser mayor o igual a 0' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  precision={2}
                  prefix="$"
                  placeholder="0.00"
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(v) => v.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="activo" label="Estado" valuePropName="checked">
            <Switch
              checkedChildren="Activo"
              unCheckedChildren="Inactivo"
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Productos;
